import { subDays } from 'date-fns';
import { observer } from 'mobx-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { Button, CircularProgress, NoSsr, Tooltip } from '@mui/material';

import useLoginDialog from 'hooks/use-login-dialog';
import useStores from 'hooks/use-stores';
import { useConfigs } from 'main/modules/configs';
import useHistoryOfChangesContext from 'pages/item/v2/hooks/use-history-of-changes-context';
import { HistoryOfChangesItem } from 'pages/item/v2/types/history-of-changes';
import isEmpty from 'utils/is-empty';

import LineChart from './LineChart';
import useStyles from './styles';
import { ChartData } from './types';

interface Props {
    title: string;
    scale?: number;
    getChart: (data: HistoryOfChangesItem[]) => ChartData | null;
}

const CHART_WIDTH = 268;
const CHART_PADDING = 8;

const TrendTooltip: React.FC<Props> = ({ getChart, title, children, scale }) => {
    const { classes } = useStyles();
    const { userStore } = useStores();
    const location = useLocation();
    const [dialog, openDialog] = useLoginDialog(location.pathname + location.search);
    const { settings } = useConfigs();

    const { data, isLoading, load } = useHistoryOfChangesContext();

    const [isOpen, setIsOpen] = useState(false);

    const chart = useMemo(() => {
        if (!userStore.userLoaded) return [];
        if (!userStore.isAuthorized && settings.limitDataEnabled) {
            return Array.from({ length: 10 }, (_, index) => ({
                score: Math.random(),
                version: index + 1,
                published: subDays(Date.now(), index)
            }));
        }
        return getChart(data.slice().reverse()) ?? [];
    }, [data, getChart, settings.limitDataEnabled, userStore.isAuthorized, userStore.userLoaded]);

    const handleOpen = useCallback(() => {
        load();
        setIsOpen(true);
    }, [load]);

    const handleClose = useCallback(() => setIsOpen(false), []);

    const tooltip = useMemo(() => {
        if (!userStore.userLoaded) return null;

        return (
            <div className={classes.content} style={{ minWidth: CHART_WIDTH, maxWidth: CHART_WIDTH }}>
                <span className={classes.title}>{title}</span>
                {isLoading ? (
                    <div className={classes.spinner}>
                        <CircularProgress />
                    </div>
                ) : isEmpty(chart, { recursive: true }) ? (
                    <span className={classes.empty}>{'No history found'}</span>
                ) : (
                    <div className={classes.chart}>
                        <NoSsr>
                            <LineChart width={CHART_WIDTH} padding={CHART_PADDING} chart={chart} scale={scale} />
                            {!userStore.isAuthorized && settings.limitDataEnabled ? (
                                <div className={classes.blur}>
                                    <Button
                                        className={classes.signIn}
                                        onClick={openDialog}
                                        centerRipple
                                        variant="contained"
                                        color="inherit"
                                    >
                                        {'Sign in to unlock'}
                                    </Button>
                                </div>
                            ) : null}
                        </NoSsr>
                    </div>
                )}
            </div>
        );
    }, [
        chart,
        classes.blur,
        classes.chart,
        classes.content,
        classes.empty,
        classes.signIn,
        classes.spinner,
        classes.title,
        isLoading,
        openDialog,
        scale,
        settings.limitDataEnabled,
        title,
        userStore.isAuthorized,
        userStore.userLoaded
    ]);

    return (
        <>
            <Tooltip
                open={userStore.userLoaded ? isOpen : false}
                onClose={handleClose}
                onOpen={handleOpen}
                title={tooltip}
                classes={{ tooltip: classes.tooltip }}
            >
                <div data-role="ref-holder">{children}</div>
            </Tooltip>
            {dialog}
        </>
    );
};

export default observer(TrendTooltip);
